import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ColorHash from 'color-hash';

import Moment from 'moment';
import { extendMoment } from 'moment-range';

import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Tooltip from 'components/common/Tooltip';

import Link from 'components/common/Link';

import pxToEm from 'styles/pxToEm';
import hexToRgb from 'styles/hexToRgb';

import dateFormat from 'config/dateFormat';

import ScheduleCalendarItem from './ScheduleCalendarItem';

const moment = extendMoment(Moment);

const styles = theme => ({
  row: {
    height: 55,
    '&:last-child $cell': {
      borderBottom: `1px solid ${theme.palette.primary.A100}`,
      paddingBottom: 22,
    },
    '&:last-child $cellName': {
      borderBottom: 'none',
    },
    '&:last-child $cellWeekDay': {
      borderBottom: 'none',
    },
  },
  rowVariantDateRange: {
    height: 83,
  },
  cell: {
    padding: 2,
    minWidth: 44,
    verticalAlign: 'top',
    borderLeft: `1px solid ${theme.palette.primary.A100}`,
    borderBottom: 'none',
    borderTop: `1px solid ${theme.palette.primary.A100}`,
    '&:last-child': {
      paddingRight: 2,
      borderRight: `1px solid ${theme.palette.primary.A100}`,
    },
    '&:first-child': {
      borderLeft: 'none',
    },
  },
  cellHidden: {
    width: 0,
    minWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
    borderLeft: 0,
    borderRight: 0,
    overflow: 'hidden',
  },
  cellActive: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.primary.A100)}, 0.5)`,
  },
  cellName: {
    left: 0,
    position: 'absolute',
    top: 'auto',
    padding: '8px 8px 8px 40px',
    minWidth: 151,
    width: 151,
    maxWidth: 151,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
    '&:first-child': {
      paddingLeft: 40,
    },
  },
  cellWeekDay: {
    left: 0,
    position: 'absolute',
    top: 'auto',
    fontSize: pxToEm(16),
    padding: '22px 24px',
    minWidth: 47,
    verticalAlign: 'middle',
    userSelect: 'none',
    '&:last-child': {
      paddingRight: 24,
    },
    [theme.breakpoints.down('xs')]: {
      paddingRight: 4,
      minWidth: 30,
    },
  },
  day: {
    fontSize: pxToEm(24),
    lineHeight: pxToEm(24, 24),
    fontWeight: 500,
    color: theme.palette.secondary[650],
  },
  weekDay: {
    textTransform: 'lowercase',
    lineHeight: pxToEm(12, 12),
  },
  iconColorHash: {
    display: 'inline-block',
    width: 6,
    height: 38,
    margin: '0 10px 0 -15px',
    borderRadius: 2,
    verticalAlign: 'middle',
    backgroundColor: theme.palette.common.white,
  },
  rowInactive: {
    backgroundColor: theme.palette.grey[100],
    '& $cellActive': {
      backgroundImage: `linear-gradient(45deg, ${theme.palette.grey[100]} 25%, ${theme.palette.validation[500]} 25%, ${theme.palette.validation[500]} 50%, ${theme.palette.grey[100]} 50%, ${theme.palette.grey[100]} 75%, ${theme.palette.validation[500]} 75%, ${theme.palette.validation[500]} 100%)`,
      backgroundSize: '14.14px 14.14px',
    },
  },
  tooltipInactive: {
    marginLeft: 8,
    verticalAlign: 'middle',
  },
  statusIconInactive: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    overflow: 'hidden',
    textIndent: '100%',
    cursor: 'pointer',
    backgroundColor: theme.palette.validation[500],
  },
});

const colorHash = new ColorHash();

const ScheduleCalendarRow = ({
  ID,
  Active,
  date,
  items,
  variant,
  caption,
  disabled,
  boundsSelector,
  onItemDrag,
  onItemStopDrag,
  loading,
  business,
  onRowHeadClick,
  onOrderLinesToggle,
  activeCellNotAllowed,
  onDisableDrag,
  onEnableDrag,
  onSign,
  classes,
  ...otherProps
}) => {
  const dateMoment = moment(date, dateFormat);
  return (
    <TableRow
      className={classNames(classes.row, {
        [classes.rowVariantDateRange]: variant === 'date-range',
        [classes.rowInactive]: Active === false,
      })}
    >
      {variant === 'single-date' &&
        <TableCell
          className={classNames(classes.cell, classes.cellName)}
        >
          <Link
            color="inherit"
            onClick={() => onRowHeadClick('Driver', {
              value: ID,
              label: ID === null ? 'Driver not assigned' : `${otherProps['First Name']} ${otherProps['Last Name']}`,
            })}
          >
            <div
              className={classes.iconColorHash}
              {...ID !== null ? {
                style: { backgroundColor: colorHash.hex(`00000${ID}`.slice(-6)) },
              } : {}}
            />
            {ID === null ? (
              'Driver not assigned'
            ) : (
              `${otherProps['First Name']} ${otherProps['Last Name']}`
            )}
            {Active === false ? (
              <Tooltip
                className={classes.tooltipInactive}
                content={
                  <Typography>
                    Inactive
                  </Typography>
                }
                placement="right"
              >
                <div
                  className={classes.statusIconInactive}
                />
              </Tooltip>
            ) : ''}
          </Link>
        </TableCell>
      }
      {variant === 'date-range' &&
        <TableCell className={classNames(classes.cell, classes.cellWeekDay)}>
          <Link
            color="inherit"
            onClick={() => {
              onRowHeadClick('Date of Delivery', dateMoment.format(dateFormat));
            }}
          >
            <Typography
              align="center"
              className={classes.day}
            >
              {dateMoment.format('DD')}
            </Typography>
            <Typography
              variant="caption"
              align="center"
              className={classes.weekDay}
            >
              {dateMoment.format('ddd')}
            </Typography>
          </Link>
        </TableCell>
      }
      {items.map(item => (
        <TableCell
          key={item.ID}
          id={item.ID}
          data-not-allowed={Active === false}
          data-cell="true"
          className={classNames(classes.cell, {
            [classes.cellHidden]: caption,
            [classes.cellActive]: item.active,
          })}
        >
          {item.items.map((order, index) => (
            <ScheduleCalendarItem
              key={order.ID}
              variant={variant}
              draggable={!caption}
              disabled={disabled}
              boundsSelector={boundsSelector}
              onDrag={onItemDrag}
              onStopDrag={onItemStopDrag}
              loading={loading}
              collapsed={
                item.items.length > 2
                || (item.items.length === 2 && index === 1)
              }
              business={business}
              onOrderLinesToggle={onOrderLinesToggle}
              notAllowed={activeCellNotAllowed}
              onDisableDrag={onDisableDrag}
              onEnableDrag={onEnableDrag}
              onSign={onSign}
              {...order}
            />
          ))}
        </TableCell>
      ))}
    </TableRow>
  );
};

ScheduleCalendarRow.propTypes = {
  ID: PropTypes.number,
  'First Name': PropTypes.string,
  'Last Name': PropTypes.string,
  date: PropTypes.string,
  variant: PropTypes.oneOf(['single-date', 'date-range']).isRequired,
  caption: PropTypes.bool,
  disabled: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.string,
  })).isRequired,
  boundsSelector: PropTypes.string,
  onItemDrag: PropTypes.func,
  onItemStopDrag: PropTypes.func,
  loading: PropTypes.bool,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  onRowHeadClick: PropTypes.func,
  onOrderLinesToggle: PropTypes.func,
  activeCellNotAllowed: PropTypes.bool,
  onSign: PropTypes.func,
  classes: PropTypes.shape({
    row: PropTypes.string.isRequired,
    rowVariantDateRange: PropTypes.string.isRequired,
    cell: PropTypes.string.isRequired,
    cellHidden: PropTypes.string.isRequired,
    cellActive: PropTypes.string.isRequired,
    cellName: PropTypes.string.isRequired,
    cellWeekDay: PropTypes.string.isRequired,
    iconColorHash: PropTypes.string.isRequired,
  }).isRequired,
};

ScheduleCalendarRow.defaultProps = {
  'First Name': null,
  'Last Name': null,
  ID: null,
  date: null,
  caption: false,
  disabled: false,
  boundsSelector: null,
  loading: false,
  activeCellNotAllowed: false,
  onItemDrag: () => {},
  onItemStopDrag: () => {},
  business: {},
  onRowHeadClick: () => {},
  onOrderLinesToggle: () => {},
  onSign: () => {},
};

export default withStyles(styles)(ScheduleCalendarRow);



// WEBPACK FOOTER //
// ./src/components/schedule/ScheduleCalendar/ScheduleCalendarRow.js